import type { OnscreenJobStatus } from '@/shared/api/project/types';

import {
  OnscreenAdminJobDetail,
  OnscreenAdminJobList,
  OnscreenAdminSort,
  OnscreenAdminSummary,
  SortOrder,
} from '@/shared/types/AdminOnscreen';

import { BASE_URL, BASE_URL_WITHOUT_PORT } from '../constants';
import { get, post } from '../requestMethods';

const URL = `${BASE_URL}/api/admin/onscreen`;

// Which jobs a list or summary covers. Times are ISO strings; one without a zone is UTC.
export type OnscreenAdminFilters = {
  dateFrom?: string;
  dateTo?: string;
  projectId?: string;
  // A job, project, user, space or runner ID, or a user's email.
  search?: string;
  statuses?: OnscreenJobStatus[];
  // The space owner or the user who started the job.
  userId?: string;
};

export type GetOnscreenAdminJobsParams = {
  limit: number;
  offset: number;
  sortBy: OnscreenAdminSort;
  sortOrder: SortOrder;
} & OnscreenAdminFilters;

type QueryEntry = [key: string, value: string | undefined];

const filterEntries = ({
  dateFrom,
  dateTo,
  projectId,
  search,
  statuses = [],
  userId,
}: OnscreenAdminFilters): QueryEntry[] => [
  ...statuses.map((status): QueryEntry => ['status', status]),
  ['date_from', dateFrom],
  ['date_to', dateTo],
  ['project_id', projectId],
  ['user_id', userId],
  ['search', search],
];

// Repeated keys for several statuses (`status=queued&status=failed`); empty values are left out.
export const buildOnscreenAdminQuery = (entries: QueryEntry[]): string => {
  const query = new URLSearchParams();
  entries.forEach(([key, value]) => {
    if (value) {
      query.append(key, value);
    }
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

// The result link may come as a path on our host, like the other project media.
export const adaptOnscreenAdminJobDetail = (job: OnscreenAdminJobDetail): OnscreenAdminJobDetail =>
  job.result_url?.startsWith('/') ? { ...job, result_url: BASE_URL_WITHOUT_PORT + job.result_url } : job;

export const ONSCREEN_ADMIN_API = {
  // Cancel a queued job and refund its minutes (409 once a runner took it).
  cancelOnscreenJobAdmin: (id: string) =>
    post<OnscreenAdminJobDetail>(`${URL}/jobs/${id}/cancel`, { adapter: adaptOnscreenAdminJobDetail }),

  getOnscreenJobAdmin: (id: string) =>
    get<OnscreenAdminJobDetail>(`${URL}/jobs/${id}`, { adapter: adaptOnscreenAdminJobDetail }),

  getOnscreenJobsAdmin: ({ limit, offset, sortBy, sortOrder, ...filters }: GetOnscreenAdminJobsParams) =>
    get<OnscreenAdminJobList>(
      `${URL}/jobs${buildOnscreenAdminQuery([
        ...filterEntries(filters),
        ['sort_by', sortBy],
        ['sort_order', sortOrder],
        ['offset', String(offset)],
        ['limit', String(limit)],
      ])}`,
    ),

  getOnscreenSummaryAdmin: (filters: OnscreenAdminFilters) =>
    get<OnscreenAdminSummary>(`${URL}/summary${buildOnscreenAdminQuery(filterEntries(filters))}`),
};
